import { z } from "zod";

/**
 * dot-separated event type (spec §5.2 / §6)。
 * 例: "session.started", "vad.speech_start", "wss.error"。
 * 各セグメントは lowercase + snake_case のみ。先頭は必ず英字。
 */
const EventTypeSchema = z.string().regex(/^[a-z]+(\.[a-z][a-z0-9_]*)+$/);

/**
 * spec §5.2 EventSchema — events.jsonl の 1 行に対応する Zod schema。
 * schemaVersion=1 で固定 (schema 変更時は R2 key layout を分ける前提)。
 */
export const SessionEventSchema = z.object({
  schemaVersion: z.literal(1),
  /** uuid v4。dedup key として buffer / server 両側で使用可 */
  id: z.string().uuid(),
  /** ISO8601 (UTC 推奨) — emit された wall-clock 時刻 */
  timestamp: z.string().datetime(),
  sessionId: z.string().min(1),
  /** session-scoped event (session.started / wss.connected など) は undefined */
  turnId: z.string().min(1).optional(),
  type: EventTypeSchema,
  /**
   * event ごとの追加情報。payload の内訳は type ごとに緩く定義 (spec §6 event catalog)。
   * 未指定時は空 object にフォールバックする (server routing の schemaVersion 検証で
   * events.jsonl の各行を parse するときの前方互換性のため)。
   */
  payload: z.record(z.string(), z.unknown()).default({}),
});

export type SessionEvent = z.infer<typeof SessionEventSchema>;

/**
 * spec §8.1 helper — emit 挿入点で共通利用する。
 * uuid v4 + ISO8601 + schemaVersion=1 を自動付与し、`turnId` は省略時にキー自体を落とす
 * (session-scoped event の schema 上 undefined と `{ turnId: undefined }` を同一に扱う都合)。
 */
export function makeEvent(
  type: string,
  sessionId: string,
  payload: Record<string, unknown>,
  turnId?: string
): SessionEvent {
  return {
    schemaVersion: 1,
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    sessionId,
    ...(turnId !== undefined ? { turnId } : {}),
    type,
    payload,
  };
}

// --- SessionSummary (spec §5.3) ---
// Task 4 で shape を追加、Task 9 で SessionEventBuffer.aggregateSummary が構築する

const LatencyStatsSchema = z.object({
  count: z.number().int().nonnegative(),
  meanRoundTripMs: z.number().nonnegative(),
  p95RoundTripMs: z.number().nonnegative(),
});

const SttSummarySchema = LatencyStatsSchema.extend({
  totalCharsProcessed: z.number().int().nonnegative(),
  totalCostUsd: z.number().nonnegative(),
  model: z.string().min(1),
});

const StructureSummarySchema = LatencyStatsSchema.extend({
  totalInputTokens: z.number().int().nonnegative(),
  totalOutputTokens: z.number().int().nonnegative(),
  totalCostUsd: z.number().nonnegative(),
  model: z.string().min(1),
});

const TtsSummarySchema = LatencyStatsSchema.extend({
  totalCharsSynthesized: z.number().int().nonnegative(),
  totalCostUsd: z.number().nonnegative(),
  model: z.string().min(1),
});

const SessionInfoSchema = z.object({
  turnCount: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime(),
  status: z.enum(["completed", "error"]),
});

const InteractionsSchema = z.object({
  bargeinCount: z.number().int().nonnegative(),
  silenceLevelUpCounts: z.object({
    level0: z.number().int().nonnegative(),
    level1: z.number().int().nonnegative(),
    level2: z.number().int().nonnegative(),
  }),
  silenceRecoveryCount: z.number().int().nonnegative(),
  wssErrorCount: z.number().int().nonnegative(),
  autoAdvanceCount: z.number().int().nonnegative(),
  micAcquireFailedCount: z.number().int().nonnegative(),
  // spec 2026-07-29 D11。.default(0) は必須 — 無いと本 spec 以前に R2 へ書かれた
  // summary.json が admin 側で parse に失敗し、一覧から丸ごと消える
  // (既存の beaconDroppedCount と同じ手口)。
  appErrorCount: z.number().int().nonnegative().default(0),
  crashCount: z.number().int().nonnegative().default(0),
  uploadFailedCount: z.number().int().nonnegative().default(0),
});

const OverflowSchema = z.object({
  droppedCount: z.number().int().nonnegative(),
  droppedByType: z.record(z.string(), z.number().int().nonnegative()).default({}),
  beaconDroppedCount: z.number().int().nonnegative().default(0),
});

export const SessionSummarySchema = z.object({
  schemaVersion: z.literal(1),
  sessionId: z.string().min(1),
  userId: z.string().min(1),
  generatedAt: z.string().datetime(),
  stt: SttSummarySchema,
  structure: StructureSummarySchema,
  tts: TtsSummarySchema,
  session: SessionInfoSchema,
  interactions: InteractionsSchema,
  overflow: OverflowSchema,
});
export type SessionSummary = z.infer<typeof SessionSummarySchema>;
